import { apiFetch } from "../apiClient";
import {
  AnalyticsEventCatalog,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
} from "./events";

interface AnalyticsUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
}

/**
 * Low-level analytics transport.
 *
 * Feature code should not import this file directly. Add product-level helpers
 * in `product.ts` so event names, host context, privacy rules, and property
 * naming stay consistent.
 */
let initialized = false;
let disabled = false;
let initializing = false;
let bootstrapAttempts = 0;
let bootstrapExhausted = false;
let lastBootstrapRearmAt = 0;
let lastBacklogFlushAt = 0;
let currentUser: AnalyticsUser | null = null;
const MAX_PENDING_EVENTS = 50;
const MAX_BOOTSTRAP_ATTEMPTS = 5;
const MAX_DELIVERY_ATTEMPTS = 4;
const CLIENT_REQUEST_TIMEOUT_MS = 5000;
const BOOTSTRAP_RETRY_DELAYS_MS = [1_000, 5_000, 15_000, 30_000];
const BOOTSTRAP_REARM_INTERVAL_MS = 60_000;
const BACKLOG_FLUSH_INTERVAL_MS = 60_000;
const DELIVERY_RETRY_DELAYS_MS = [250, 1_000, 5_000];
const MAX_OUTBOX_AGE_MS = 24 * 60 * 60 * 1000;
const ANALYTICS_SESSION_KEY = "finsterAnalytics.sessionId";
const ANALYTICS_SEQUENCE_KEY = "finsterAnalytics.sequence";
// One storage key per queued event. A single shared key would require a
// read-modify-write to add or remove one entry, and task panes for different
// presentations are separate runtimes sharing this origin's storage, so two of
// them could read the same snapshot and overwrite each other's queued events.
const ANALYTICS_OUTBOX_KEY_PREFIX = "finsterAnalytics.outbox.";
const ANALYTICS_LAST_USER_KEY = "finsterAnalytics.lastUserId";

type AnalyticsPublicConfig = {
  analytics?: {
    enabled?: boolean;
    transport?: string;
  };
};
type PendingAnalyticsEvent = {
  event: AnalyticsEventName;
  properties: AnalyticsEventProperties[AnalyticsEventName];
  insertId: string;
  time: number;
  sessionId: number;
  sequence: number;
  // Who the event belongs to, or `null` only when nobody has ever signed in on
  // this install. Used solely to decide whether a stored event may still be
  // delivered; it is never sent, as the server derives identity from the session.
  userId: string | null;
};

/**
 * Outcome of one delivery attempt.
 *
 * `rejected` means the server will never accept this payload, so it is dropped
 * rather than retried forever across reloads. `deferred` means delivery may yet
 * succeed, so the event stays in the outbox for a later attempt.
 */
type DeliveryResult = "delivered" | "rejected" | "deferred";

// The outbox is durable so events survive the task-pane reload that otherwise
// discards everything raised before bootstrap and authentication complete.
// Storage is the source of truth; these mirrors are the fallback for hosts
// where Web Storage throws.
let memoryOutbox: PendingAnalyticsEvent[] = [];
let outboxStorageAvailable = true;
let memorySequence = 0;
let memoryLastUserId: string | null = null;
const inFlightInsertIds = new Set<string>();

/**
 * Lists required schema properties that are absent from an event payload.
 *
 * @param event - Catalog event whose schema should be checked.
 * @param properties - Properties supplied for the event.
 * @returns Required property names whose values are missing or empty.
 */
function getMissingRequiredProperties<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventProperties[E]
): string[] {
  const record = properties as unknown as Record<string, unknown>;
  return AnalyticsEventCatalog[event].requiredProperties.filter((property) => {
    const value = record[property];
    return value === undefined || value === null || value === "";
  });
}

/**
 * Creates the stable identifier Amplitude uses to deduplicate delivery retries.
 *
 * @returns A UUID when Web Crypto supports it, otherwise a timestamp-based identifier.
 */
function createInsertId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Gets the task-pane session start timestamp used as Amplitude's session ID.
 *
 * @returns A positive millisecond timestamp shared by events in the current browser session.
 */
function getAnalyticsSessionId(): number {
  try {
    const existing = Number.parseInt(
      window.sessionStorage.getItem(ANALYTICS_SESSION_KEY) || "",
      10
    );
    if (Number.isSafeInteger(existing) && existing > 0) return existing;

    const created = Date.now();
    window.sessionStorage.setItem(ANALYTICS_SESSION_KEY, String(created));
    return created;
  } catch {
    return Date.now();
  }
}

/**
 * Issues the next position for this analytics session.
 *
 * The sequence is assigned when the event is created, not when it is sent, so a
 * gap in the numbers Amplitude receives identifies an event that was raised but
 * never delivered. Without it a dropped event is indistinguishable from one that
 * never happened, which makes the delivery loss rate unmeasurable.
 *
 * @returns A positive, monotonically increasing position within the session.
 */
function nextAnalyticsSequence(): number {
  try {
    const previous = Number.parseInt(
      window.sessionStorage.getItem(ANALYTICS_SEQUENCE_KEY) || "",
      10
    );
    const next = Number.isSafeInteger(previous) && previous > 0 ? previous + 1 : 1;
    window.sessionStorage.setItem(ANALYTICS_SEQUENCE_KEY, String(next));
    return next;
  } catch {
    memorySequence += 1;
    return memorySequence;
  }
}

/**
 * Records who the add-in is signing in, so events raised later can be attributed
 * even when nobody is currently signed in.
 *
 * @param userId - Authenticated user identifier.
 * @returns Nothing.
 */
function rememberUser(userId: string): void {
  memoryLastUserId = userId;
  try {
    window.localStorage.setItem(ANALYTICS_LAST_USER_KEY, userId);
  } catch {
    // The in-memory value still covers this task-pane session.
  }
}

/**
 * Identifies who an event being raised now belongs to.
 *
 * Falls back to the last user seen on this install, so an event raised in the
 * gap around sign-in or sign-out is still owned rather than being handed to
 * whoever authenticates next. `null` means nobody has ever signed in here.
 *
 * @returns The owning user identifier, or `null` when there has never been one.
 */
function getOwningUserId(): string | null {
  if (currentUser) return currentUser.id;
  try {
    return window.localStorage.getItem(ANALYTICS_LAST_USER_KEY) || memoryLastUserId;
  } catch {
    return memoryLastUserId;
  }
}

/**
 * Determines whether a stored value is still a well-formed queued event.
 *
 * Outbox entries are read back from untrusted storage and may have been written
 * by an earlier build, so an unknown event name is treated as unusable.
 *
 * @param value - Candidate parsed from storage.
 * @returns Whether the value can be delivered as-is.
 */
function isPendingAnalyticsEvent(value: unknown): value is PendingAnalyticsEvent {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.event === "string" &&
    Object.prototype.hasOwnProperty.call(AnalyticsEventCatalog, candidate.event) &&
    !!candidate.properties &&
    typeof candidate.properties === "object" &&
    typeof candidate.insertId === "string" &&
    Number.isSafeInteger(candidate.time) &&
    Number.isSafeInteger(candidate.sessionId) &&
    Number.isSafeInteger(candidate.sequence) &&
    (candidate.userId === null || typeof candidate.userId === "string")
  );
}

function outboxKeyFor(insertId: string): string {
  return `${ANALYTICS_OUTBOX_KEY_PREFIX}${insertId}`;
}

/**
 * Lists the storage keys currently holding queued events.
 *
 * The snapshot is taken before any deletion so callers can remove entries while
 * iterating without skipping their neighbours.
 *
 * @returns Outbox keys present in storage.
 */
function listOutboxKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(ANALYTICS_OUTBOX_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    outboxStorageAvailable = false;
  }
  return keys;
}

function discardOutboxKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    outboxStorageAvailable = false;
  }
}

/**
 * Reads undelivered events, deleting malformed and expired entries as it goes.
 *
 * Pruning is written back immediately rather than left for a later add or
 * remove, so an outbox holding nothing but stale entries is actually emptied
 * instead of being re-read and re-reported on every flush.
 *
 * Because each event is stored under its own key, an entry that cannot be
 * parsed costs only itself; it cannot take the rest of the queue with it.
 *
 * @returns Queued events that are still worth attempting, oldest first.
 */
function loadOutbox(): PendingAnalyticsEvent[] {
  if (!outboxStorageAvailable) return [...memoryOutbox];

  const cutoff = Date.now() - MAX_OUTBOX_AGE_MS;
  const live: PendingAnalyticsEvent[] = [];
  let expired = 0;

  for (const key of listOutboxKeys()) {
    let entry: unknown;
    try {
      entry = JSON.parse(window.localStorage.getItem(key) || "");
    } catch {
      discardOutboxKey(key);
      continue;
    }
    if (!isPendingAnalyticsEvent(entry)) {
      discardOutboxKey(key);
      continue;
    }
    if (entry.time < cutoff) {
      discardOutboxKey(key);
      expired += 1;
      continue;
    }
    live.push(entry);
  }

  if (expired > 0) {
    console.warn("[Analytics] Discarded events that outlived the outbox retention", { expired });
  }

  // Delivery order is creation order. Sequences are per session, so time is the
  // only comparable ordering across the sessions an outbox can span.
  return live.sort((a, b) => a.time - b.time || a.sequence - b.sequence);
}

/**
 * Enforces the outbox bound by discarding the oldest events.
 *
 * @returns Nothing.
 */
function enforceOutboxLimit(): void {
  const entries = loadOutbox();
  const excess = entries.length - MAX_PENDING_EVENTS;
  if (excess <= 0) return;

  console.warn("[Analytics] Outbox is full; dropping the oldest events", { dropped: excess });
  for (const entry of entries.slice(0, excess)) {
    removeFromOutbox(entry.insertId);
  }
}

/**
 * Persists one queued event under its own key.
 *
 * @param payload - Analytics event awaiting delivery.
 * @returns Nothing.
 */
function addToOutbox(payload: PendingAnalyticsEvent): void {
  if (outboxStorageAvailable) {
    try {
      window.localStorage.setItem(outboxKeyFor(payload.insertId), JSON.stringify(payload));
      enforceOutboxLimit();
      return;
    } catch {
      // Storage is still readable, so adopt what it holds before falling back to
      // memory; otherwise everything queued so far becomes unreachable.
      const stranded = loadOutbox();
      outboxStorageAvailable = false;
      memoryOutbox = [...stranded, payload].slice(-MAX_PENDING_EVENTS);
      return;
    }
  }
  memoryOutbox = [...memoryOutbox, payload].slice(-MAX_PENDING_EVENTS);
}

function removeFromOutbox(insertId: string): void {
  memoryOutbox = memoryOutbox.filter((entry) => entry.insertId !== insertId);
  if (!outboxStorageAvailable) return;
  discardOutboxKey(outboxKeyFor(insertId));
}

function clearOutbox(): void {
  memoryOutbox = [];
  if (!outboxStorageAvailable) return;
  for (const key of listOutboxKeys()) discardOutboxKey(key);
}

/**
 * Determines whether a failed delivery is safe to retry immediately.
 *
 * @param response - Failed HTTP response, or `null` when no response was received.
 * @returns `true` for network failures, rate limits, and server errors.
 */
function shouldRetry(response: Response | null): boolean {
  return response === null || response.status === 429 || response.status >= 500;
}

/**
 * Determines whether a failure is worth another attempt in a later session.
 *
 * The endpoint answers 401 or 403 when the session has expired or has not been
 * established yet, which is the most common way a legitimate event fails. Those
 * events must be held rather than discarded, even though retrying within the
 * next few seconds would not help.
 *
 * @param response - Failed HTTP response.
 * @returns `true` when the same payload could succeed once the user is authenticated again.
 */
function isRecoverableFailure(response: Response | null): boolean {
  return response !== null && (response.status === 401 || response.status === 403);
}

/**
 * Performs a fetch that is aborted when the client analytics timeout elapses.
 *
 * @param path - First-party API path to request.
 * @param init - Standard fetch request options.
 * @returns The HTTP response.
 * @throws When the request fails or exceeds the configured timeout.
 */
async function fetchWithTimeout(path: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CLIENT_REQUEST_TIMEOUT_MS);
  try {
    return await apiFetch(path, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * Delivers one analytics payload to the authenticated server endpoint.
 *
 * Retries transient failures with the same `insertId`, allowing Amplitude to
 * deduplicate repeated delivery attempts.
 *
 * @param payload - Validated catalog event, properties, timestamp, and deduplication ID.
 * @returns Whether the event was delivered, permanently rejected, or should be retried later.
 */
async function postAnalyticsEvent(payload: PendingAnalyticsEvent): Promise<DeliveryResult> {
  const requestBody = JSON.stringify({
    eventType: payload.event,
    eventProperties: payload.properties,
    insertId: payload.insertId,
    time: payload.time,
    sessionId: payload.sessionId,
    sequence: payload.sequence,
  });

  let lastResponse: Response | null = null;
  for (let attempt = 0; attempt < MAX_DELIVERY_ATTEMPTS; attempt += 1) {
    try {
      lastResponse = await fetchWithTimeout("/api/analytics/events", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestBody,
        keepalive: true,
      });
      if (lastResponse.ok) return "delivered";
    } catch {
      lastResponse = null;
    }

    if (!shouldRetry(lastResponse)) {
      return isRecoverableFailure(lastResponse) ? "deferred" : "rejected";
    }
    if (attempt === MAX_DELIVERY_ATTEMPTS - 1) break;
    await new Promise((resolve) => window.setTimeout(resolve, DELIVERY_RETRY_DELAYS_MS[attempt]));
  }

  return "deferred";
}

/**
 * Starts asynchronous delivery and retires the outbox entry once it is settled.
 *
 * A deferred event is deliberately left in the outbox so the next boot retries
 * it; the stable `insertId` lets Amplitude discard the duplicate if the original
 * attempt did land after all.
 *
 * @param payload - Analytics event to deliver.
 * @returns Nothing; delivery runs asynchronously.
 */
function sendAnalyticsEvent(payload: PendingAnalyticsEvent): void {
  if (inFlightInsertIds.has(payload.insertId)) return;
  inFlightInsertIds.add(payload.insertId);

  void postAnalyticsEvent(payload)
    .then((result) => {
      if (result === "deferred") {
        console.warn("[Analytics] Event deferred for a later attempt", {
          event: payload.event,
          sequence: payload.sequence,
        });
        return;
      }
      if (result === "rejected") {
        console.warn("[Analytics] Event rejected by the server and dropped", {
          event: payload.event,
          sequence: payload.sequence,
        });
      }
      removeFromOutbox(payload.insertId);
    })
    .catch((error) => {
      // The event stays in the outbox so an unexpected transport fault is not a
      // silent loss, and so it cannot reject unhandled into the product flow.
      console.warn("[Analytics] Event delivery failed unexpectedly", {
        event: payload.event,
        sequence: payload.sequence,
        error,
      });
    })
    .finally(() => {
      inFlightInsertIds.delete(payload.insertId);
    });
}

/**
 * Sends outbox events once both server configuration and authenticated user context are ready.
 *
 * @returns Nothing.
 */
function flushPendingEvents(): void {
  if (!initialized || !currentUser) return;
  const signedInUserId = currentUser.id;
  lastBacklogFlushAt = Date.now();

  for (const payload of loadOutbox()) {
    // An event owned by someone else cannot be delivered, because the server
    // would attribute it to whoever is signed in now. Unowned events predate any
    // sign-in on this install, so they belong to the first user to arrive.
    if (payload.userId !== null && payload.userId !== signedInUserId) {
      console.warn("[Analytics] Discarding an event raised by a different user", {
        event: payload.event,
        sequence: payload.sequence,
      });
      removeFromOutbox(payload.insertId);
      continue;
    }
    sendAnalyticsEvent(payload);
  }
}

/**
 * Reattempts the backlog while the add-in is still running.
 *
 * Without this a deferred event waits for the next task-pane reload, which can
 * be hours away. Activity drives the retry, throttled so a burst of events does
 * not replay the whole outbox each time.
 *
 * @returns Nothing.
 */
function retryOutboxBacklog(): void {
  if (Date.now() - lastBacklogFlushAt < BACKLOG_FLUSH_INTERVAL_MS) return;
  flushPendingEvents();
}

/**
 * Schedules the next bounded attempt to load analytics configuration.
 *
 * @returns Nothing.
 */
function scheduleBootstrapRetry(): void {
  if (bootstrapAttempts >= MAX_BOOTSTRAP_ATTEMPTS) {
    bootstrapExhausted = true;
    console.warn(
      "[Analytics] Server configuration remained unavailable; the next tracked event will retry"
    );
    return;
  }
  const delay =
    BOOTSTRAP_RETRY_DELAYS_MS[
      Math.min(bootstrapAttempts - 1, BOOTSTRAP_RETRY_DELAYS_MS.length - 1)
    ];
  window.setTimeout(() => {
    void bootstrapAnalytics();
  }, delay);
}

/**
 * Restarts bootstrap after its retry budget was spent while the add-in kept running.
 *
 * The original schedule gives up after roughly a minute, so a task pane that was
 * opened offline would stay uninitialized for the rest of its life and silently
 * accumulate events. Activity is the signal to try again, throttled so a burst of
 * events does not become a burst of config requests.
 *
 * @param options.ignoreThrottle - Retry even if one was attempted recently. Used
 * for one-off moments like sign-in, which cannot arrive in bursts and would
 * otherwise leave the backlog stuck until the next tracked event.
 * @returns Nothing.
 */
function rearmBootstrapIfExhausted(options: { ignoreThrottle?: boolean } = {}): void {
  if (!bootstrapExhausted || initialized || disabled || initializing) return;

  const now = Date.now();
  if (!options.ignoreThrottle && now - lastBootstrapRearmAt < BOOTSTRAP_REARM_INTERVAL_MS) return;

  lastBootstrapRearmAt = now;
  bootstrapAttempts = 0;
  bootstrapExhausted = false;
  void bootstrapAnalytics();
}

/**
 * Loads public analytics configuration and enables the server transport.
 *
 * Explicitly disabled analytics clear queued events. Transient configuration
 * failures are retried using the bounded bootstrap schedule.
 *
 * @returns A promise that resolves when the current bootstrap attempt finishes.
 */
export async function bootstrapAnalytics(): Promise<void> {
  if (initialized || disabled || initializing) return;

  initializing = true;
  bootstrapAttempts += 1;
  try {
    const response = await fetchWithTimeout("/api/config/public", {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(`Config endpoint returned HTTP ${response.status}`);
    }
    const data = (await response.json()) as AnalyticsPublicConfig;
    const analyticsEnabled =
      data.analytics?.enabled === true && data.analytics?.transport === "server";
    if (!analyticsEnabled) {
      disabled = true;
      clearOutbox();
      console.info("[Analytics] Analytics disabled by server configuration");
      return;
    }

    initialized = true;
    flushPendingEvents();
  } catch {
    scheduleBootstrapRetry();
  } finally {
    initializing = false;
  }
}

/**
 * Marks an authenticated user as available and releases queued analytics events.
 *
 * Identity values are not sent from the browser; the server derives trusted
 * identity and organization context from the authenticated session.
 *
 * @param user - Authenticated add-in user.
 * @returns Nothing.
 */
export function identifyAnalyticsUser(user: AnalyticsUser): void {
  currentUser = user;
  rememberUser(user.id);
  // Sign-in is the point at which a backlog becomes deliverable, so it also has
  // to revive a bootstrap that already gave up. Flushing alone does nothing
  // while the client is uninitialized, which would leave events queued during an
  // outage stuck until the next tracked event or a full reload.
  rearmBootstrapIfExhausted({ ignoreThrottle: true });
  flushPendingEvents();
}

/**
 * Clears local user context without discarding undelivered events.
 *
 * This runs on sign-out but also on every mount while authentication is still
 * resolving, so it must not empty the outbox: doing so would erase the previous
 * session's events before they ever get a chance to flush. Events that genuinely
 * cannot be delivered are instead dropped at flush time, once there is a signed-in
 * user to compare them against.
 *
 * @returns Nothing.
 */
export function resetAnalyticsClient(): void {
  currentUser = null;
}

/**
 * Wraps a catalog event with the session context and identifiers fixed at creation time.
 *
 * The timestamp, session, and sequence are captured now rather than at send time
 * so a queued event still reports when and where it actually happened, even if it
 * is delivered from a later task-pane session.
 *
 * @param event - Catalog event name.
 * @param properties - Typed properties for the selected event.
 * @returns A transport-ready analytics payload.
 */
function buildAnalyticsPayload<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventProperties[E]
): PendingAnalyticsEvent {
  return {
    event,
    properties,
    insertId: createInsertId(),
    time: Date.now(),
    sessionId: getAnalyticsSessionId(),
    sequence: nextAnalyticsSequence(),
    userId: getOwningUserId(),
  };
}

/**
 * Validates and records a typed product analytics event.
 *
 * Every event is written to the durable outbox before delivery is attempted, so
 * one that cannot be sent yet is retried on a later boot instead of being lost
 * with the page.
 *
 * @param event - Catalog event name.
 * @param properties - Typed properties defined for the selected event.
 * @returns Nothing.
 */
export function trackAnalyticsEvent<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventProperties[E]
): void {
  const missingProperties = getMissingRequiredProperties(event, properties);
  if (missingProperties.length > 0) {
    console.warn("[Analytics] Event missing required properties", {
      event,
      missingProperties,
    });
  }

  if (disabled) return;

  const payload = buildAnalyticsPayload(event, properties);
  addToOutbox(payload);

  if (!initialized || !currentUser) {
    rearmBootstrapIfExhausted();
    return;
  }
  sendAnalyticsEvent(payload);
  retryOutboxBacklog();
}
